import React from 'react';
import moment from 'moment-timezone';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Participation, SessionFormat } from '@app/api';
import { LabeledIconChip, LabeledSection, SessionFormatChip } from '@app/core';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  mainInfoLine: {
    border: 'solid 1px',
    borderColor: theme.colors.grayBorder,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
    gap: theme.marginBase * 2,
    display: 'flex',
    flexWrap: 'wrap',
    [theme.media.mobile]: {
      flexDirection: 'column',
    },
  },

  linkContainer: {
    display: 'flex',
    gap: theme.marginBase * 2,
    width: '100%',
    [theme.media.mobile]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  link: {
    flex: 1,
    [theme.media.mobile]: {
      flex: 'none',
      width: '100%',
    },
  },

  inFlexBlock: {
    '&:last-child': {
      marginRight: 0,
    },
  },
  timezone: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
}));

interface Props {
  participation: Participation;
}

export const AppointmentInfoLine = ({ participation }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { session, appointment } = participation;
  const { format, participant } = participation;

  if (!session || !format || !appointment) {
    return null;
  }
  const isInParticipantPlace = format === SessionFormat.IN_PARTICIPANT_PLACE;

  const timezone = participant?.timezone || moment.tz.guess();
  const mDate = moment.tz(appointment.date, timezone);
  const endDate = moment
    .tz(appointment.date, timezone)
    .add(participation.duration || session.duration, 'minutes');

  return (
    <div className={classes.mainInfoLine}>
      <LabeledIconChip
        label={t('booked.summary.date')}
        labelColor="green"
        color="ink"
        chipIcon="ri-calendar-event-line"
        chipLabel={mDate.format('LLL')}
      />
      <LabeledIconChip
        label={t('booked.summary.time')}
        labelColor="green"
        color="ink"
        chipIcon="ri-time-line"
        chipLabel={mDate.format('LT') + ' - ' + endDate.format('LT')}
      />
      <LabeledIconChip
        label={t('booked.summary.timezone')}
        labelColor="inkSecondary"
        color="ink"
        chipLabel={timezone}
      />
      {participation.participantAddress && isInParticipantPlace && (
        <LabeledIconChip
          label={t('booked.summary.address')}
          labelColor="inkSecondary"
          color="ink"
          chipIcon="ri-map-pin-2-line"
          chipLabel={participation.participantAddress}
        />
      )}
      <LabeledSection label={t('booked.summary.format')} color="ink">
        <SessionFormatChip participation={participation} color="inkSecondary" />
      </LabeledSection>
    </div>
  );
};
